import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { Observable, forkJoin } from 'rxjs';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import {
  BackofficeInformationRequestCard,
  FILE_OBJECT_TYPE_INFO_REQUEST_CALCULATION,
  FILE_OBJECT_TYPE_INFO_REQUEST_INVOICE,
  InformationRequestBackofficeStatus,
  InformationRequestCalculationListByInformationRequestIdItem,
  InformationRequestInvoiceListByInformationRequestIdItem,
  InvoiceDeliveryMethod
} from 'types/information-request.interface';
import type { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import { formatDate, formatDatetime } from 'util/date.utility';
import { TranslateService } from '@ngx-translate/core';
import { runUploads, isolateUploads, countUploadFailures } from 'util/file-upload.utility';
import { IrInvoiceMarkPaidModalComponent, InvoiceMarkPaidPayload } from './modals/mark-paid-modal/mark-paid-modal.component';
import { IrInvoiceMarkSentModalComponent, InvoiceMarkSentPayload } from './modals/mark-sent-modal/mark-sent-modal.component';
import { IrInvoiceSendEmailModalComponent, InvoiceSendEmailPayload } from './modals/send-email-modal/send-email-modal.component';
import { IrInvoiceSendPortalModalComponent } from './modals/send-portal-modal/send-portal-modal.component';

const ULID_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
const ULID_LENGTH = 26;
const MAX_PAYMENT_NOTE_LENGTH = 255;

const INVOICE_LOCKED_STATUSES: InformationRequestBackofficeStatus[] = [
  'Draft',
  'NewPortal',
  'NewBackoffice',
  'NewRedirected',
  'Completed',
  'Cancelled'
];
const SEND_INVOICE_EXCLUDED_STATUSES: InformationRequestBackofficeStatus[] = ['Completed', 'Cancelled', 'NewRedirected'];

function generateUlid(): string {
  return Array.from({ length: ULID_LENGTH }, () => ULID_ALPHABET[Math.floor(Math.random() * ULID_ALPHABET.length)]).join('');
}

@Component({
  selector: 'app-ir-invoice',
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TooltipComponent,
    FileUploadComponent,
    IrInvoiceSendEmailModalComponent,
    IrInvoiceSendPortalModalComponent,
    IrInvoiceMarkSentModalComponent,
    IrInvoiceMarkPaidModalComponent
  ],
  templateUrl: './ir-invoice.component.html',
  styleUrls: ['../../form/base-form.scss', './ir-invoice.component.scss']
})
export class IrInvoiceComponent implements OnChanges {
  readonly formatDate = formatDate;
  readonly formatDatetime = formatDatetime;
  readonly fileTypeCalculation = FILE_OBJECT_TYPE_INFO_REQUEST_CALCULATION;
  readonly fileTypeInvoice = FILE_OBJECT_TYPE_INFO_REQUEST_INVOICE;

  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() calculations: InformationRequestCalculationListByInformationRequestIdItem[] = [];
  @Input() invoices: InformationRequestInvoiceListByInformationRequestIdItem[] = [];
  @Input() structuralUnits: StructuralUnitMinimalListItem[] = [];
  @Output() invoiceChanged = new EventEmitter<void>();

  sortedInvoices: InformationRequestInvoiceListByInformationRequestIdItem[] = [];
  sortedCalculations: InformationRequestCalculationListByInformationRequestIdItem[] = [];

  isPaymentEditMode = false;
  paymentForm: FormGroup;

  isAddingCalculation = false;
  newCalculationId = '';
  newCalculationDescription = '';
  isSubmittingNewCalculationFiles = false;
  editingCalculationId: string | null = null;
  editingCalculationDescription = '';
  isSubmittingCalculationFiles = false;

  isAddingInvoice = false;
  newInvoiceId = '';
  newInvoiceDescription = '';
  newInvoiceFileCount = 0;
  isSubmittingNewInvoiceFiles = false;
  editingInvoiceId: string | null = null;
  editingInvoiceDescription = '';
  isSubmittingInvoiceFiles = false;

  isActionLoading = false;

  activeInvoice: InformationRequestInvoiceListByInformationRequestIdItem | null = null;
  isSendEmailModalOpen = false;
  isSendPortalModalOpen = false;
  isMarkSentModalOpen = false;
  isMarkPaidModalOpen = false;

  @ViewChild('editInvoiceFileUpload') editInvoiceFileUpload?: FileUploadComponent;

  get hasUnsavedChanges(): boolean {
    return (
      this.isPaymentEditMode ||
      this.isAddingCalculation ||
      this.editingCalculationId !== null ||
      this.isAddingInvoice ||
      this.editingInvoiceId !== null
    );
  }

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private fb: FormBuilder
  ) {
    this.paymentForm = this.fb.group({
      prepaymentApplies: [false],
      postpaymentApplies: [false],
      noChargeApplies: [false],
      paymentNote: ['', Validators.maxLength(MAX_PAYMENT_NOTE_LENGTH)]
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['informationRequest']) {
      this.resetPaymentForm();
    }
    if (changes['invoices']) {
      this.sortedInvoices = [...(this.invoices || [])].sort((a, b) => a.ordinalNumber - b.ordinalNumber);
    }
    if (changes['calculations']) {
      this.sortedCalculations = [...(this.calculations || [])].sort((a, b) => {
        const aTime = new Date(a.modifiedOn || a.createdOn).getTime();
        const bTime = new Date(b.modifiedOn || b.createdOn).getTime();
        return bTime - aTime;
      });
    }
  }

  get canMarkPaid(): boolean {
    return this.informationRequest?.paymentState === 'AwaitingPayment';
  }

  get markPaidTooltip(): string {
    if (this.canMarkPaid) return '';
    return this.informationRequest?.paymentState === 'Paid'
      ? 'pages.viewInfoRequest.invoice.markPaidAlreadyPaidTooltip'
      : 'pages.viewInfoRequest.invoice.markPaidDisabledTooltip';
  }

  get canEditInvoicing(): boolean {
    return !!this.informationRequest && !INVOICE_LOCKED_STATUSES.includes(this.informationRequest.backofficeStatus);
  }

  get canSendInvoice(): boolean {
    return !!this.informationRequest && !SEND_INVOICE_EXCLUDED_STATUSES.includes(this.informationRequest.backofficeStatus);
  }

  private resetPaymentForm(): void {
    const req = this.informationRequest;
    this.paymentForm.reset({
      prepaymentApplies: !!req?.prepaymentApplies,
      postpaymentApplies: !!req?.postpaymentApplies,
      noChargeApplies: !!req?.noChargeApplies,
      paymentNote: req?.paymentNote || ''
    });
    this.isPaymentEditMode = false;
  }

  // --- Payment types ---

  get selectedPaymentTypes(): string[] {
    const req = this.informationRequest;
    const selected: string[] = [];
    if (req?.prepaymentApplies) selected.push('pages.viewInfoRequest.invoice.paymentType.prepayment');
    if (req?.postpaymentApplies) selected.push('pages.viewInfoRequest.invoice.paymentType.postpayment');
    if (req?.noChargeApplies) selected.push('pages.viewInfoRequest.invoice.paymentType.noCharge');
    return selected;
  }

  togglePaymentEdit(): void {
    this.isPaymentEditMode = true;
  }

  cancelPaymentEdit(): void {
    this.resetPaymentForm();
  }

  savePaymentType(): void {
    if (!this.informationRequest?.id || this.isActionLoading || this.paymentForm.invalid) return;
    const { prepaymentApplies, postpaymentApplies, noChargeApplies, paymentNote } = this.paymentForm.getRawValue();
    const trimmedNote = (paymentNote || '').trim() || null;

    const previous = {
      prepaymentApplies: this.informationRequest.prepaymentApplies,
      postpaymentApplies: this.informationRequest.postpaymentApplies,
      noChargeApplies: this.informationRequest.noChargeApplies,
      paymentNote: this.informationRequest.paymentNote
    };
    this.informationRequest.prepaymentApplies = prepaymentApplies;
    this.informationRequest.postpaymentApplies = postpaymentApplies;
    this.informationRequest.noChargeApplies = noChargeApplies;
    this.informationRequest.paymentNote = trimmedNote;
    this.isPaymentEditMode = false;
    this.isActionLoading = true;

    this.apiService
      .updateIrPaymentTypes(this.informationRequest.id, {
        informationRequestId: this.informationRequest.id,
        prepaymentApplies,
        postpaymentApplies,
        noChargeApplies,
        paymentNote: trimmedNote
      })
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.invoiceChanged.emit();
        },
        error: () => {
          this.informationRequest.prepaymentApplies = previous.prepaymentApplies;
          this.informationRequest.postpaymentApplies = previous.postpaymentApplies;
          this.informationRequest.noChargeApplies = previous.noChargeApplies;
          this.informationRequest.paymentNote = previous.paymentNote;
          this.resetPaymentForm();
          this.isPaymentEditMode = true;
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  // --- Calculations ---

  startAddCalculation(): void {
    this.isAddingCalculation = true;
    this.newCalculationDescription = '';
    this.newCalculationId = generateUlid();
  }

  cancelAddCalculation(): void {
    this.isAddingCalculation = false;
    this.newCalculationDescription = '';
    this.newCalculationId = '';
  }

  confirmAddCalculation(): void {
    if (!this.informationRequest?.id || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService
      .createIrCalculation({
        informationRequestId: this.informationRequest.id,
        description: this.newCalculationDescription.trim() || null
      })
      .subscribe({
        next: (created) => {
          this.isActionLoading = false;
          this.newCalculationId = created.id;
          this.isSubmittingNewCalculationFiles = true;
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  startEditCalculation(calc: InformationRequestCalculationListByInformationRequestIdItem): void {
    this.editingCalculationId = calc.id;
    this.editingCalculationDescription = calc.description || '';
  }

  cancelEditCalculation(): void {
    this.editingCalculationId = null;
    this.editingCalculationDescription = '';
  }

  confirmEditCalculation(): void {
    if (!this.editingCalculationId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.isSubmittingCalculationFiles = true;
  }

  deleteCalculation(id: string): void {
    if (this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.deleteIrCalculation(id).subscribe({
      next: () => {
        this.isActionLoading = false;
        this.invoiceChanged.emit();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
      }
    });
  }

  private notifyUploadFailures(results: unknown[]): void {
    const failed = countUploadFailures(results);
    if (failed > 0) {
      const message = this.translateService.instant('components.notification.fileUploadPartialError', {
        failed,
        total: results.length
      });
      this.toastService.showDanger(message, '', { shouldTranslate: false });
    }
  }

  handleNewCalculationFileUploads(uploads: Observable<unknown>[]): void {
    const finish = () => {
      this.isSubmittingNewCalculationFiles = false;
      this.cancelAddCalculation();
      this.invoiceChanged.emit();
    };
    if (uploads.length === 0) {
      queueMicrotask(finish);
      return;
    }
    runUploads(uploads).subscribe(results => {
      this.notifyUploadFailures(results);
      finish();
    });
  }

  handleEditCalculationFileUploads(uploads: Observable<unknown>[]): void {
    if (!this.editingCalculationId) return;
    const update$ = this.apiService.updateIrCalculation({
      id: this.editingCalculationId,
      description: this.editingCalculationDescription.trim() || null
    });
    forkJoin([update$, ...isolateUploads(uploads)]).subscribe({
      next: results => {
        this.notifyUploadFailures(results.slice(1));
        this.isActionLoading = false;
        this.isSubmittingCalculationFiles = false;
        this.cancelEditCalculation();
        this.invoiceChanged.emit();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
        this.isSubmittingCalculationFiles = false;
      }
    });
  }

  // --- Invoices ---

  startAddInvoice(): void {
    this.isAddingInvoice = true;
    this.newInvoiceDescription = '';
    this.newInvoiceId = generateUlid();
    this.newInvoiceFileCount = 0;
  }

  cancelAddInvoice(): void {
    this.isAddingInvoice = false;
    this.newInvoiceDescription = '';
    this.newInvoiceId = '';
    this.newInvoiceFileCount = 0;
  }

  onNewInvoiceFilesChanged(payload: { files: File[] }): void {
    this.newInvoiceFileCount = payload.files.length;
  }

  confirmAddInvoice(): void {
    if (!this.informationRequest?.id || this.isActionLoading) return;
    if (this.newInvoiceFileCount === 0) {
      this.toastService.showDanger('invoiceFileRequired', '', { shouldTranslate: true });
      return;
    }
    this.isActionLoading = true;
    this.apiService
      .createIrInvoice({
        informationRequestId: this.informationRequest.id,
        description: this.newInvoiceDescription.trim() || null
      })
      .subscribe({
        next: (created) => {
          this.isActionLoading = false;
          this.newInvoiceId = created.id;
          this.isSubmittingNewInvoiceFiles = true;
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  startEditInvoice(invoice: InformationRequestInvoiceListByInformationRequestIdItem): void {
    this.editingInvoiceId = invoice.id;
    this.editingInvoiceDescription = invoice.description || '';
  }

  cancelEditInvoice(): void {
    this.editingInvoiceId = null;
    this.editingInvoiceDescription = '';
  }

  confirmEditInvoice(): void {
    if (!this.editingInvoiceId || this.isActionLoading) return;
    if ((this.editInvoiceFileUpload?.totalFileCount ?? 0) === 0) {
      this.toastService.showDanger('invoiceFileRequired', '', { shouldTranslate: true });
      return;
    }
    this.isActionLoading = true;
    this.isSubmittingInvoiceFiles = true;
  }

  canDeleteInvoice(invoice: InformationRequestInvoiceListByInformationRequestIdItem): boolean {
    return (invoice.sendingEvents?.length || 0) === 0;
  }

  deleteInvoice(invoice: InformationRequestInvoiceListByInformationRequestIdItem): void {
    if (!this.canDeleteInvoice(invoice) || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.deleteIrInvoice(invoice.id).subscribe({
      next: () => {
        this.isActionLoading = false;
        this.invoiceChanged.emit();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
      }
    });
  }

  handleNewInvoiceFileUploads(uploads: Observable<unknown>[]): void {
    const finish = () => {
      this.isSubmittingNewInvoiceFiles = false;
      this.cancelAddInvoice();
      this.invoiceChanged.emit();
    };
    if (uploads.length === 0) {
      queueMicrotask(finish);
      return;
    }
    runUploads(uploads).subscribe(results => {
      this.notifyUploadFailures(results);
      finish();
    });
  }

  handleEditInvoiceFileUploads(uploads: Observable<unknown>[]): void {
    if (!this.editingInvoiceId) return;
    const update$ = this.apiService.updateIrInvoice({
      id: this.editingInvoiceId,
      description: this.editingInvoiceDescription.trim() || null
    });
    forkJoin([update$, ...isolateUploads(uploads)]).subscribe({
      next: results => {
        this.notifyUploadFailures(results.slice(1));
        this.isActionLoading = false;
        this.isSubmittingInvoiceFiles = false;
        this.cancelEditInvoice();
        this.invoiceChanged.emit();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
        this.isSubmittingInvoiceFiles = false;
      }
    });
  }

  // --- Send/mark modals ---

  openSendEmailModal(invoice: InformationRequestInvoiceListByInformationRequestIdItem): void {
    this.activeInvoice = invoice;
    this.isSendEmailModalOpen = false;
    setTimeout(() => {
      this.isSendEmailModalOpen = true;
    });
  }

  onSendEmailConfirmed(payload: InvoiceSendEmailPayload): void {
    if (!this.activeInvoice || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService
      .sendIrInvoiceByEmail({
        invoiceId: this.activeInvoice.id,
        recipient: payload.recipient,
        emailSubject: payload.emailSubject,
        emailBody: payload.emailBody
      })
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.isSendEmailModalOpen = false;
          this.activeInvoice = null;
          this.invoiceChanged.emit();
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  openSendPortalModal(invoice: InformationRequestInvoiceListByInformationRequestIdItem): void {
    this.activeInvoice = invoice;
    this.isSendPortalModalOpen = false;
    setTimeout(() => {
      this.isSendPortalModalOpen = true;
    });
  }

  onSendPortalConfirmed(): void {
    if (!this.activeInvoice || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService
      .sendIrInvoiceToPortal({
        invoiceId: this.activeInvoice.id
      })
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.isSendPortalModalOpen = false;
          this.activeInvoice = null;
          this.invoiceChanged.emit();
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  openMarkSentModal(invoice: InformationRequestInvoiceListByInformationRequestIdItem): void {
    this.activeInvoice = invoice;
    this.isMarkSentModalOpen = false;
    setTimeout(() => {
      this.isMarkSentModalOpen = true;
    });
  }

  onMarkSentConfirmed(payload: InvoiceMarkSentPayload): void {
    if (!this.activeInvoice || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService
      .markIrInvoiceSent({
        invoiceId: this.activeInvoice.id,
        actualDeliveryMethod: payload.actualDeliveryMethod,
        addressDetail: payload.addressDetail,
        backofficeNote: payload.backofficeNote
      })
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.isMarkSentModalOpen = false;
          this.activeInvoice = null;
          this.invoiceChanged.emit();
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  // --- Mark paid ---

  openMarkPaidModal(): void {
    this.isMarkPaidModalOpen = false;
    setTimeout(() => {
      this.isMarkPaidModalOpen = true;
    });
  }

  onMarkPaidConfirmed(payload: InvoiceMarkPaidPayload): void {
    if (!this.informationRequest?.id || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService
      .markIrAsPaid(this.informationRequest.id, {
        informationRequestId: this.informationRequest.id,
        ...(payload.mustCompleteByDate ? { mustCompleteByDate: payload.mustCompleteByDate } : {})
      })
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.isMarkPaidModalOpen = false;
          this.invoiceChanged.emit();
        },
        error: (err) => {
          this.toastService.showValidationOrRequestError(err);
          this.isActionLoading = false;
        }
      });
  }

  // --- Helpers ---

  getInvoiceDeliveryMethodLabel(method: InvoiceDeliveryMethod): string {
    if (!method || method === 'Unknown') return '-';
    return `pages.viewInfoRequest.invoice.deliveryMethod.${method}`;
  }

  getInvoiceDeliveryDetail(): string {
    const req = this.informationRequest;
    if (!req) return '';
    if (req.invoiceDeliveryMethod === 'Archive') {
      return req.invoiceDeliveryStructuralUnitName || '';
    }
    return req.invoiceDeliveryDetail || '';
  }

  getActualDeliveryMethodLabel(method: string | null | undefined): string {
    if (!method) return '-';
    return `pages.viewInfoRequest.invoice.deliveryMethod.${method}`;
  }
}
